import { Link } from "react-router-dom";
import { Ticket, Search, ShieldCheck, Users, ArrowRight, Sparkles, Clock, Mail, MessageCircle } from "lucide-react";

const bullets = [
  { icon: Ticket, title: "Submit a ticket", desc: "Tell us what's going on. We reply fast." },
  { icon: Search, title: "Track any request", desc: "Ticket number + email = live status." },
  { icon: Users, title: "Dedicated agents", desc: "A focused team of up to agents owns every ticket end-to-end." },
];

export default function Landing() {
  return (
    <div className="workspace-grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-3 py-1">
              <Sparkles className="w-3.5 h-3.5" /> Human support, focused
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05] font-display">
              Support that actually{" "}
              <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
                answers you.
              </span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              Pixel Support is a streamlined helpdesk solution designed to foster seamless
              collaboration among agents. Customers submit tickets, which are addressed promptly,
              while the entire team remains aligned through real-time updates. Built for
              efficiency, transparency, and reliability, Pixel Support ensures that every customer
              receives attentive service and every agent operates with clarity and confidence.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/register"
                data-testid="landing-submit-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
              >
                Create an account <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login"
                data-testid="landing-login-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-slate-300 text-slate-800 text-sm font-semibold hover:bg-slate-50 hover:border-slate-400 transition-all active:scale-[0.98]"
              >
                Sign in
              </Link>
              <Link
                to="/track"
                data-testid="landing-track-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-slate-600 text-sm font-semibold hover:text-slate-900 transition-all"
              >
                Track existing ticket
              </Link>
            </div>

            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {bullets.map((b) => (
                <div key={b.title} className="beacon-card p-4">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 grid place-items-center border border-blue-100">
                    <b.icon className="w-5 h-5" />
                  </div>
                  <div className="mt-3 font-semibold text-slate-900 text-sm">{b.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{b.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-tr from-blue-200/60 via-white to-teal-200/60 rounded-3xl blur-xl" />
              <div className="relative beacon-card p-6 sm:p-7 shadow-xl">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" /> How it works
                </div>
                <h3 className="mt-3 text-lg font-bold text-slate-900 font-display">
                  Three steps. That's it.
                </h3>

                <ol className="mt-5 space-y-4">
                  <li className="flex gap-3">
                    <span className="shrink-0 w-8 h-8 rounded-lg bg-blue-50 text-blue-700 border border-blue-100 grid place-items-center font-bold text-sm">1</span>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">Submit a ticket</div>
                      <div className="text-xs text-slate-500 mt-0.5">Tell us what's wrong. You'll get a ticket number and a confirmation email instantly.</div>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="shrink-0 w-8 h-8 rounded-lg bg-amber-50 text-amber-700 border border-amber-100 grid place-items-center font-bold text-sm">2</span>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">An agent picks it up</div>
                      <div className="text-xs text-slate-500 mt-0.5">A human on our team owns your ticket end-to-end — no bots, no ticket ping-pong.</div>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="shrink-0 w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 grid place-items-center font-bold text-sm">3</span>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">Get updates in your inbox</div>
                      <div className="text-xs text-slate-500 mt-0.5">Replies land in your email. You can also check status any time with your ticket number.</div>
                    </div>
                  </li>
                </ol>

                <div className="mt-6 grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                      <Clock className="w-3.5 h-3.5" /> First reply
                    </div>
                    <div className="mt-1 font-display font-bold text-slate-900 text-lg leading-tight">Within hours</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                      <MessageCircle className="w-3.5 h-3.5" /> Handled by
                    </div>
                    <div className="mt-1 font-display font-bold text-slate-900 text-lg leading-tight">Real humans</div>
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  Your ticket and email are only visible to the assigned support agents.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
